import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type GuildPermission, type GuildRoleResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface RoleInput {
  name: string;
  color?: string | null;
  permissions?: GuildPermission[];
}

interface UseGuildRolesReturn {
  roles: GuildRoleResponse[];
  loading: boolean;
  error: string | null;
  createRole: (input: RoleInput) => Promise<GuildRoleResponse>;
  updateRole: (roleId: string, input: Partial<RoleInput>) => Promise<GuildRoleResponse>;
  deleteRole: (roleId: string) => Promise<void>;
  assignRole: (userId: string, roleId: string) => Promise<void>;
  unassignRole: (userId: string, roleId: string) => Promise<void>;
}

/**
 * The guild's custom roles. Mutations invalidate both the role list AND the
 * member list — a role's `memberCount` and a member's role chips are two views
 * of the same assignment, so refreshing one without the other shows stale data.
 */
export function useGuildRoles(guildId: string | undefined): UseGuildRolesReturn {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.guilds.roles(guildId ?? '');

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => api.listGuildRoles(guildId!),
    enabled: !!guildId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: queryKeys.guilds.members(guildId ?? '') });
    queryClient.invalidateQueries({ queryKey: queryKeys.guilds.permissions(guildId ?? '') });
  };

  const createMut = useMutation({
    mutationFn: (input: RoleInput) => api.createGuildRole(guildId!, input),
    onSuccess: invalidate,
  });

  const updateMut = useMutation({
    mutationFn: ({ roleId, input }: { roleId: string; input: Partial<RoleInput> }) =>
      api.updateGuildRole(guildId!, roleId, input),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (roleId: string) => api.deleteGuildRole(guildId!, roleId),
    onSuccess: invalidate,
  });

  const assignMut = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.assignGuildRole(guildId!, userId, roleId),
    onSuccess: invalidate,
  });

  const unassignMut = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.unassignGuildRole(guildId!, userId, roleId),
    onSuccess: invalidate,
  });

  return {
    roles: data?.roles ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load roles' : null,
    createRole: async (input) => (await createMut.mutateAsync(input)).role,
    updateRole: async (roleId, input) => (await updateMut.mutateAsync({ roleId, input })).role,
    deleteRole: (roleId) => deleteMut.mutateAsync(roleId),
    assignRole: (userId, roleId) => assignMut.mutateAsync({ userId, roleId }),
    unassignRole: (userId, roleId) => unassignMut.mutateAsync({ userId, roleId }),
  };
}
